import { BEIGE_300, CORAL_600, GRAY_700, GRAY_900, MOBILE_VIEW_WIDTH_QUERY } from 'constants/styling/theme'
import { FC, Fragment } from 'react'
import { Link, Stack, Typography } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import { getPrivacyPolicyLink, getTermsLink } from 'utils/localization'
import i18n, { transformLanguageToAuth0Value } from 'translations/i18n'

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import { PageTransition } from 'utils/animations'
import { Path } from 'constants/router'
import { SignupCard } from './SignupCard/SignupCard.component'
import { StatusPage } from 'components/common/StatusPage'
import { logAnalyticsEvent } from 'utils/analytics'
import { getOnboardingCTLink } from 'utils/localization/getOnboardingCTLink'
import signup_client from 'assets/img/landing/profile-client.webp'
import signup_creative from 'assets/img/landing/profile-creative.webp'
import { useAuth0 } from 'utils/auth'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useNavigate } from 'react-router-dom'

/**
 * @component
 * SignupPortal page, allowing users to sign up or continue to the dashboard.
 * 
 * This component uses the upbranding design.
 *
 * @example
 * <SignupPortal />
 */
export const SignupPortal: FC = () => {
  const { loginWithRedirect, isAuthenticated, user, logout } = useAuth0()
  const { t } = useTranslation(['signup_portal', 'terms_and_privacy_footer'])
  const navigate = useNavigate()

  const isMobileView = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)
  const params = new URLSearchParams(window.location.search)

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.href } })
  }

  return (
    <PageTransition>
      <Stack
        alignItems="center"
        justifyContent="center"
        position="relative"
        bgcolor={BEIGE_300}
        padding={isMobileView ? '2rem' : '0'}
        height={isMobileView ? '100%' : '100vh'}
      >
        {!isAuthenticated && (
          <Stack
            sx={{
              top: '2rem',
              left: '2rem',
              gap: '0.8rem',
              cursor: 'pointer',
              position: 'absolute',
              alignItems: 'center',
              flexDirection: 'row',
            }}
            onClick={() => navigate(-1)}
          >
            <ArrowBackRoundedIcon sx={{ color: CORAL_600, fontSize: '1.8rem' }} />
            <Typography component="a" variant="text-sm" fontWeight="bold" color={GRAY_900}>
              {t('back_to_previous_page')}
            </Typography>
          </Stack>
        )}

        {isAuthenticated ? (
          <StatusPage
            title={t('sign_up')}
            descriptionTrans={{
              i18nKey: t('user_logged_in_message', { username: user?.name }),
              components: [<strong />]
            }}
            primaryButton={{
              text: t('continue_to_dashboard'),
              onClick: () => navigate(Path.INDEX)
            }}
            secondaryButton={{
              text: t('authentication:log_out'),
              onClick: handleLogout
            }}
          />
        ) : (
          <Fragment>

            <Stack gap="6.4rem" alignItems="center" marginBottom="4rem">

              <Typography variant="display-md" marginTop={isMobileView ? '4rem' : '0'}>
                {t('choose_profile')}
              </Typography>

              <Stack direction="row" flexWrap="wrap" gap="2.4rem">

                <SignupCard
                  title={t('client')}
                  image={signup_client}
                  description={t('client_des')}
                  buttonText={t('client_signup')}
                  onClick={() => {
                    logAnalyticsEvent('clicks_sign_up', { location: 'SignupPortal' })
                    loginWithRedirect({
                      appState: { targetUrl: Path.INDEX },
                      authorizationParams: {
                        mode: 'signUp',
                        language: transformLanguageToAuth0Value(i18n.language),
                        login_hint: params.get('userEmail') ?? undefined,
                      },
                    })
                  }}
                />

                <SignupCard
                  title={t('creative')}
                  image={signup_creative}
                  description={t('creative_des')}
                  buttonText={t('creative_signup')}
                  onClick={() => {
                    window.open(getOnboardingCTLink(i18n.language), '_self', 'noopener noreferrer')
                  }}
                  note={
                    <Typography variant="text-xs" color={GRAY_700}>
                      <Trans t={t} i18nKey="creative_note">
                        {t('creative_note')}
                      </Trans>
                    </Typography>
                  }
                />

              </Stack>

            </Stack>

            <Stack
              marginX="2rem"
              textAlign="center"
              bottom={!isMobileView ? '2rem' : '0'}
              position={!isMobileView ? 'absolute' : 'inherit'}
            >
              <Typography variant="text-xs" color={GRAY_700}>
                {`${t('terms_and_privacy_footer:terms_agreement')} `}
                <Link variant="text-xs" color={GRAY_900} underline="none" href={getTermsLink(i18n.language)}>
                  {t('terms_and_privacy_footer:terms_and_conditions')}
                </Link>
                {` ${t('terms_and_privacy_footer:and')} `}
                <Link variant="text-xs" color={GRAY_900} underline="none" href={getPrivacyPolicyLink(i18n.language)}>
                  {t('terms_and_privacy_footer:privacy_policy')}
                </Link>
              </Typography>
            </Stack>
          </Fragment>
        )}

      </Stack>
    </PageTransition>
  )
}
